import {
  Box,
  Code,
  Divider,
  Heading,
  HeadingProps,
  Image,
  ListItem,
  OrderedList,
  Text,
  UnorderedList
} from '@chakra-ui/react'
import {MDXProvider} from '@mdx-js/react'

import Link from 'components/link'
import PartialBleed, {bleedSize} from 'components/partial-bleed'
import {navSize, space} from 'config/theme'
import toKebabCase from 'utils/to-kebab-case'

type MDXWrapperProps = {
  children: JSX.Element | JSX.Element[]
}

type CString = {
  children: string
}

type AProps = CString & {
  href: string
}

// Anchor
const Anchor = ({id}: {id: string}) => (
  <Box
    as='a'
    display='block'
    id={id}
    position='relative'
    top={{
      base: `${parseInt(navSize.base) * -2}px`,
      md: `${parseInt(navSize.md) * -2}px`
    }}
    visibility='hidden'
  />
)

// Use a common spacing between elements
const pt = space.md

const LinkedHeading = ({children, ...props}: HeadingProps & CString) => {
  const id = children ? toKebabCase(String(children)) : ''
  return (
    <Heading {...props} position='relative'>
      <Anchor id={id} /> {children}
    </Heading>
  )
}

export const components = {
  a({children, href}: AProps) {
    return <Link href={href}>{children}</Link>
  },
  blockquote(p: any) {
    return (
      <PartialBleed>
        <Box
          as='blockquote'
          bg='blue.500'
          borderWidth='1px'
          borderColor='blue.500'
          color='white'
          fontSize='xl'
          fontWeight='bold'
          pt='1.5rem'
          px={bleedSize}
          pb={0}
          my='2.5rem'
          rounded='sm'
          shadow='2xl'
          textAlign='center'
          textShadow='2px 2px 10px rgba(0,0,0, 0.25)'
        >
          {p.children}
        </Box>
      </PartialBleed>
    )
  },
  h1({children}: CString) {
    return (
      <LinkedHeading size='2xl' mt={space.lg} mb={space.md}>
        {children}
      </LinkedHeading>
    )
  },
  h2({children}: CString) {
    return (
      <LinkedHeading size='xl' mt={space.lg} mb={space.sm}>
        {children}
      </LinkedHeading>
    )
  },
  h3({children}: CString) {
    return (
      <LinkedHeading size='lg' mt={space.lg} mb={space.sm}>
        {children}
      </LinkedHeading>
    )
  },
  h4({children}: CString) {
    return (
      <LinkedHeading size='md' my={pt}>
        {children}
      </LinkedHeading>
    )
  },
  h5({children}: CString) {
    return (
      <LinkedHeading size='sm' my={pt}>
        {children}
      </LinkedHeading>
    )
  },
  h6({children}: CString) {
    return (
      <LinkedHeading size='xs' my={pt}>
        {children}
      </LinkedHeading>
    )
  },
  hr() {
    return <Divider />
  },
  p({children}: CString) {
    return <Text mb={pt}>{children}</Text>
  },
  ul(p: any) {
    return <UnorderedList {...p} />
  },
  ol(p: any) {
    return <OrderedList {...p} />
  },
  li(p: any) {
    return <ListItem mb={space.xs} {...p} />
  },
  code(p: any) {
    return (
      <PartialBleed>
        <Code
          color='white'
          backgroundColor='blue.800'
          mb={space.sm}
          overflowX='auto'
          p={bleedSize}
          rounded='md'
          width='100%'
          {...p}
        />
      </PartialBleed>
    )
  },
  inlineCode(p: any) {
    return <Code colorScheme='blue' {...p} />
  },
  img(p: any) {
    return (
      <PartialBleed>
        <Image
          alt={p.alt}
          fallback={<Box height='400px' />}
          shadow='2xl'
          {...p}
        />
      </PartialBleed>
    )
  }
}

/**
 * Wrap MDX components with common components.
 */
export default function MDXWrapper({children}: MDXWrapperProps) {
  return <MDXProvider components={components}>{children}</MDXProvider>
}
